import { supabase } from './supabase';

let cachedToken: string | null = null;
let cachedTokenExp = 0;
let activeRefresh: Promise<string> | null = null;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} duurde te lang (>${Math.round(ms / 1000)}s)`)),
      ms,
    );
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

function decodeJwtExp(token: string): number {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return (payload.exp || 0) * 1000;
  } catch {
    return 0;
  }
}

function cacheToken(token: string) {
  cachedToken = token;
  cachedTokenExp = decodeJwtExp(token);
}

async function doRefresh(): Promise<string> {
  // Primary: getUser() triggers a server-side token refresh and is more
  // reliable than refreshSession() which is known to hang in some browsers.
  try {
    const {
      data: { user },
    } = await withTimeout(supabase.auth.getUser(), 5_000, 'Sessie valideren');
    if (user) {
      const {
        data: { session },
      } = await withTimeout(supabase.auth.getSession(), 3_000, 'Sessie ophalen');
      if (session?.access_token) {
        cacheToken(session.access_token);
        return session.access_token;
      }
    }
  } catch {
    /* fall through to refreshSession */
  }

  // Fallback: refreshSession() with a strict timeout
  const {
    data: { session },
  } = await withTimeout(supabase.auth.refreshSession(), 5_000, 'Sessie vernieuwen');
  if (session?.access_token) {
    cacheToken(session.access_token);
    return session.access_token;
  }
  cachedToken = null;
  cachedTokenExp = 0;
  throw new Error('Sessie verlopen. Log opnieuw in.');
}

async function refreshOnce(): Promise<string> {
  if (activeRefresh) return activeRefresh;
  activeRefresh = doRefresh().finally(() => {
    activeRefresh = null;
  });
  return activeRefresh;
}

async function getToken(): Promise<string> {
  if (cachedToken && Date.now() < cachedTokenExp - 60_000) {
    return cachedToken;
  }

  const {
    data: { session },
  } = await withTimeout(supabase.auth.getSession(), 5_000, 'Sessie ophalen');
  if (session?.access_token) {
    const exp = decodeJwtExp(session.access_token);
    if (Date.now() < exp - 60_000) {
      cacheToken(session.access_token);
      return session.access_token;
    }
  }

  return refreshOnce();
}

export function clearTokenCache() {
  cachedToken = null;
  cachedTokenExp = 0;
}

function buildHeaders(token: string): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
}

const SLOW_EDGE_FUNCTIONS = new Set([
  // Exact lezen: één maand bankdagboek + factuurbrug; Exact laat 60
  // verzoeken/minuut toe, dus een maand kan tientallen seconden duren.
  'exact-lezen',
  'analyze-facade-box',
  'analyze-gevel-meetstaat',
  'ai-training-analyse',
  'analyze-damage',
  'analyze-gebouwdelen',
  'analyze-receipt',
  'auto-detect-markers',
  'extract-document-data',
  'generate-oa-contract-pdf',
  'generate-cluster-pdf',
  'generate-meerwerk-pdf',
  'generate-houtrot-resultaat-pdf',
  'generate-offerte-pdf',
  'generate-opname-pdf',
  'name-voortgang-markers',
  'ai-chat',
  'chief',
  'chief-voice-setup',
  'email-ai-assist',
  'reanalyze-purchase-invoice',
  'manage-purchase-subscription',
  'seed-project-from-lead',
]);

const MEDIUM_EDGE_FUNCTIONS = new Set(['kvk-search', 'suggest-appointment', 'chief-voice-session']);

function getTimeoutMs(name: string): number {
  if (SLOW_EDGE_FUNCTIONS.has(name)) return 180_000;
  if (MEDIUM_EDGE_FUNCTIONS.has(name)) return 30_000;
  return 15_000;
}

export async function fetchEdgeFunction(
  name: string,
  body: Record<string, unknown>,
): Promise<Response> {
  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`;
  const timeoutMs = getTimeoutMs(name);

  const token = await getToken();

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: 'POST',
      headers: buildHeaders(token),
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (err: unknown) {
    clearTimeout(timer);
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error(
        `${name} duurde te lang (>${Math.round(timeoutMs / 1000)}s). Probeer het opnieuw.`,
      );
    }
    throw err;
  }
  clearTimeout(timer);

  if (response.status === 401) {
    const origBody = await response.text();

    // Invalidate cached token so refreshOnce fetches a fresh one.
    // Keep activeRefresh intact so concurrent 401s share the same refresh.
    cachedToken = null;
    cachedTokenExp = 0;

    try {
      const freshToken = await withTimeout(refreshOnce(), 8_000, 'Sessie vernieuwen (retry)');
      const retryController = new AbortController();
      const retryTimer = setTimeout(() => retryController.abort(), timeoutMs);
      try {
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: buildHeaders(freshToken),
          body: JSON.stringify(body),
          signal: retryController.signal,
        });
      } finally {
        clearTimeout(retryTimer);
      }
      return response;
    } catch {
      return new Response(origBody, { status: 401 });
    }
  }

  return response;
}

let fallbackListeners: Array<(msg: string) => void> = [];

export function onAiFallback(listener: (msg: string) => void): () => void {
  fallbackListeners.push(listener);
  return () => {
    fallbackListeners = fallbackListeners.filter((l) => l !== listener);
  };
}

function notifyFallback(msg: string) {
  fallbackListeners.forEach((l) => l(msg));
}

export async function callEdgeFunction(
  name: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const response = await fetchEdgeFunction(name, body);

  const text = await response.text();
  let result: Record<string, unknown>;
  try {
    result = JSON.parse(text);
  } catch {
    throw new Error(`Server error (${response.status}): ${text.substring(0, 200)}`);
  }

  if (!response.ok) {
    throw new Error(
      String(
        result.error || result.message || result.msg || `Verzoek mislukt (${response.status})`,
      ),
    );
  }

  // Voice-functies geven 'provider' terug als gewone config (elevenlabs/openai)
  // — dat is géén AI-fallback-signaal.
  const VOICE_FUNCTIES = ['chief-voice-session', 'chief-voice-setup'];
  const provider = VOICE_FUNCTIES.includes(name)
    ? result._ai_provider
    : result.provider || result._ai_provider;
  if (provider === 'openai') {
    notifyFallback(
      'Anthropic AI was niet beschikbaar. Resultaat is via OpenAI (fallback) gegenereerd.',
    );
  }

  return result;
}
